// F1 Statistics Calculator Module
// Handles statistics calculations for drivers, constructors, and circuits

/**
 * Add points to drivers (take max for cumulative championship standings)
 * @param {Object} data - F1 data object
 * @returns {Object} - Enhanced data with driver points
 */
export const addDriverPoints = (data) => {
  if (!data.drivers || !data.driver_standings) return data;

  const driverPoints = {};
  data.driver_standings.forEach(standing => {
    const driverId = standing.driverId;
    const points = standing.points || 0;
    // Take maximum points (cumulative championship standings)
    if (!driverPoints[driverId] || points > driverPoints[driverId]) {
      driverPoints[driverId] = points;
    }
  });

  data.drivers = data.drivers.map(driver => ({
    ...driver,
    points: driverPoints[driver.driverId] || 0
  }));

  return data;
};

/**
 * Calculate constructor statistics (championships, wins, poles)
 * @param {Object} data - F1 data object
 * @returns {Object} - Enhanced data with constructor statistics
 */
export const calculateConstructorStatistics = (data) => {
  if (!data.constructors || !data.constructor_standings || !data.results || !data.qualifying || !data.races) {
    return data;
  }

  // Create race year mapping
  const raceYearMap = {};
  data.races.forEach(race => {
    raceYearMap[race.raceId] = race.year;
  });

  // Calculate championships for each constructor
  const constructorChampionships = {};
  data.constructor_standings.forEach(standing => {
    const constructorId = standing.constructorId;
    const year = raceYearMap[standing.raceId];
    if (!year) return;

    // Group by year and constructor
    const yearKey = `${year}-${constructorId}`;
    if (!constructorChampionships[yearKey]) {
      constructorChampionships[yearKey] = {
        constructorId,
        year,
        position: standing.position,
        points: standing.points || 0,
        wins: standing.wins || 0,
        raceId: standing.raceId
      };
    } else {
      // Keep the entry with the highest raceId (last race of the year - final standings)
      if (standing.raceId > constructorChampionships[yearKey].raceId) {
        constructorChampionships[yearKey] = {
          constructorId,
          year,
          position: standing.position,
          points: standing.points || 0,
          wins: standing.wins || 0,
          raceId: standing.raceId
        };
      }
    }
  });

  // Count championships (position = 1) for each constructor
  const championshipsByConstructor = {};
  Object.values(constructorChampionships).forEach(({ constructorId, position }) => {
    if (position === 1) {
      championshipsByConstructor[constructorId] = (championshipsByConstructor[constructorId] || 0) + 1;
    }
  });

  // Calculate wins for each constructor
  const constructorWins = {};
  data.results.forEach(result => {
    if (result.position === 1) {
      const constructorId = result.constructorId;
      constructorWins[constructorId] = (constructorWins[constructorId] || 0) + 1;
    }
  });

  // Calculate poles for each constructor
  const constructorPoles = {};
  data.qualifying.forEach(qual => {
    if (qual.position === 1) {
      const constructorId = qual.constructorId;
      constructorPoles[constructorId] = (constructorPoles[constructorId] || 0) + 1;
    }
  });

  // Add statistics to constructors
  data.constructors = data.constructors.map(constructor => ({
    ...constructor,
    championships: championshipsByConstructor[constructor.constructorId] || 0,
    wins: constructorWins[constructor.constructorId] || 0,
    poles: constructorPoles[constructor.constructorId] || 0
  }));

  return data;
};

/**
 * Calculate driver statistics (championships, wins)
 * @param {Object} data - F1 data object
 * @returns {Object} - Enhanced data with driver statistics
 */
export const calculateDriverStatistics = (data) => {
  if (!data.drivers || !data.driver_standings || !data.results || !data.races) return data;

  // Create race year mapping
  const raceYearMap = {};
  data.races.forEach(race => {
    raceYearMap[race.raceId] = race.year;
  });

  // Calculate championships for each driver
  const driverChampionships = {};
  data.driver_standings.forEach(standing => {
    const driverId = standing.driverId;
    const year = raceYearMap[standing.raceId];
    if (!year) return;

    // Group by year and driver
    const yearKey = `${year}-${driverId}`;
    if (!driverChampionships[yearKey]) {
      driverChampionships[yearKey] = {
        driverId,
        year,
        position: standing.position,
        points: standing.points || 0,
        wins: standing.wins || 0,
        raceId: standing.raceId
      };
    } else {
      // Keep the entry with the highest raceId (last race of the year - final standings)
      if (standing.raceId > driverChampionships[yearKey].raceId) {
        driverChampionships[yearKey] = {
          driverId,
          year,
          position: standing.position,
          points: standing.points || 0,
          wins: standing.wins || 0,
          raceId: standing.raceId
        };
      }
    }
  });

  // Count championships (position = 1) for each driver
  const championshipsByDriver = {};
  Object.values(driverChampionships).forEach(({ driverId, position }) => {
    if (position === 1) {
      championshipsByDriver[driverId] = (championshipsByDriver[driverId] || 0) + 1;
    }
  });

  // Calculate wins for each driver from results data
  const driverWins = {};
  data.results.forEach(result => {
    if (result.position === 1) {
      const driverId = result.driverId;
      driverWins[driverId] = (driverWins[driverId] || 0) + 1;
    }
  });

  // Add statistics to drivers
  data.drivers = data.drivers.map(driver => ({
    ...driver,
    championships: championshipsByDriver[driver.driverId] || 0,
    wins: driverWins[driver.driverId] || 0
  }));

  return data;
};

/**
 * Apply all statistics calculations
 * @param {Object} data - F1 data object
 * @returns {Object} - Data with all statistics calculated
 */
export const applyStatisticsCalculations = (data) => {
  let enhancedData = { ...data };
  
  enhancedData = addDriverPoints(enhancedData);
  enhancedData = calculateConstructorStatistics(enhancedData);
  enhancedData = calculateDriverStatistics(enhancedData);
  
  return enhancedData;
};
